import { NullableTableColumns } from "@flaminem/ui-components";
import React from "react";

import { AlertListConfig } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.types";
import { HeadlessWidget } from "src/dynamic-view-renderer/headlessWidgets/HeadlessWidget.typings";
import { HeadlessWidgetRenderer } from "src/dynamic-view-renderer/headlessWidgets/HeadlessWidgetRenderer";
import { AlertsSortingCriterion } from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { AlertSummary } from "src/types/alert";
import { CaseTypeDefinition } from "src/types/case";

export type UseAlertsTableProps = {
  alerts: AlertSummary[];
  onScenarioClick?: (alert: AlertSummary) => void;
};

export const stopPropagation = (e: React.MouseEvent) => {
  e.stopPropagation();
};

export const useGetColumns = ({
  onScenarioClick,
  alertList,
  headlessWidgets,
  caseTypeDefinitions,
}: {
  onScenarioClick?: (alert: AlertSummary) => void;
  alertList: AlertListConfig;
  headlessWidgets: HeadlessWidget[];
  caseTypeDefinitions: CaseTypeDefinition[];
}) => {
  const { columns, sortByMap, reverseSortByMap } = React.useMemo(() => {
    if (!alertList?.columns || !headlessWidgets) {
      return { columns: {}, sortByMap: {}, reverseSortByMap: {} };
    }

    const sortByMap: Record<string, AlertsSortingCriterion> = {};
    const reverseSortByMap: Partial<Record<AlertsSortingCriterion, string>> = {};
    const columns = alertList.columns.reduce((acc, column) => {
      const widget = headlessWidgets.find((w) => w.code === column.headlessWidgetCode);
      if (!widget) return acc;

      if (column.sortBy) {
        const criterion = column.sortBy as AlertsSortingCriterion;
        sortByMap[column.headlessWidgetCode] = criterion;
        reverseSortByMap[criterion] = column.headlessWidgetCode;
      }

      return {
        ...acc,
        [column.headlessWidgetCode]: {
          label: column.headerLabel,
          flexGrow: 1,
          flexShrink: 1,
          width: column.width || 1,
          className: "text-sm",
          render: (row: AlertSummary) => (
            <HeadlessWidgetRenderer
              widget={widget}
              data={{
                root: row,
                elements: row.currentCase?.elements,
                propertyValues: row.currentCase?.properties,
                caseTypeDefinitions: caseTypeDefinitions,
              }}
              onClick={widget.type === "scenario" ? () => onScenarioClick?.(row) : undefined}
            />
          ),
          sorter: column.sorter,
        },
      };
    }, {} as NullableTableColumns<AlertSummary>);

    return { columns, sortByMap, reverseSortByMap };
  }, [alertList, headlessWidgets, onScenarioClick, caseTypeDefinitions]);

  return { columns, sortByMap, reverseSortByMap };
};
